import type { RequestInfo } from '@warp-drive/core/types/request';
import { buildBaseURL, type QueryUrlOptions } from '@warp-drive/utilities';
import { SkipCache } from '@warp-drive/core-types/request';

/**
 * Plan action request builders.
 *
 * The v1 PlanResource stopped serializing tiers as an inline attribute in
 * Dec 2025 (tio-api replaced `attribute :tiers` with `relationship :tiers`),
 * so a plan loaded through /me carries no tier data. These builders read the
 * related-tier records directly from the JSONAPI relationship route that
 * replaced the attribute.
 */

const plansUrlOptions: QueryUrlOptions = {
  identifier: { type: 'plans' },
  op: 'query',
  resourcePath: 'plans',
};

function plansUrl(path?: string): string {
  const base = buildBaseURL(plansUrlOptions);
  return path ? `${base}/${path.replace(/^\/+/, '')}` : base;
}

/**
 * GET /plans/:id/tiers (jsonapi_related_resources :tiers)
 *
 * Raw JSONAPI document (`{ data: [{ attributes: { fromCycle, amount, ... } }] }`,
 * amount in cents); the call site reads it off `content`. Tier order is not
 * guaranteed by the API — sort by fromCycle before use (the removed inline
 * attribute was ordered fromCycle ASC).
 */
export function planTiersRequest(planId: string): RequestInfo {
  return {
    url: plansUrl(`${planId}/tiers`),
    method: 'GET',
    cacheOptions: { [SkipCache]: true },
  };
}

/**
 * A waiting period as the summary reports it: `unit` is a moment.js-style unit
 * ('days', 'weeks', 'months', 'years') matching the plan's waiting_period_unit.
 */
export interface PlanSummaryWaitingPeriod {
  amount: number;
  unit: string;
}

/** A tier amount the plan takes from the employee record rather than the tier. */
export interface PlanSummaryCustomAmount {
  type: 'custom';
  amount_field: string;
  percentage_field?: string | null;
  minimum?: number | null;
  maximum?: number | null;
}

/** Cents, or the custom-amount descriptor when the tier defers to the employee. */
export type PlanSummaryAmountValue = number | PlanSummaryCustomAmount;

export interface PlanSummaryTier {
  tier: number;
  amount: PlanSummaryAmountValue;
  from_cycle: number;
  /** Absent on the last tier, which runs indefinitely. */
  to_cycle?: number | null;
  per: string;
  maximum?: number | null;
}

export interface PlanSummaryContributionAmount {
  /** 'flat' when one amount covers every cycle, 'tiered' when it steps. */
  type: string;
  amount?: PlanSummaryAmountValue | null;
  /** Set on a flat plan that stops paying after a fixed number of cycles. */
  ends_after_cycles?: number | null;
  tiers?: PlanSummaryTier[] | null;
}

/**
 * The plan summary as tio-payment renders it. Snake_case because the tio-api
 * route proxies the body through untouched, and every field is optional: the
 * endpoint serializes with exclude_none, so an unset value is an ABSENT key
 * rather than a null one.
 */
export interface PlanSummaryResponse {
  plan_name?: string;
  plan_type?: string;
  cadence?: string;
  plan_description?: string;
  contribution_start_date?: string;
  contribution_amount?: PlanSummaryContributionAmount;
  waiting_period?: PlanSummaryWaitingPeriod;
  /** Cents. Read from tio_limit.limits, NOT the legacy plan column. */
  annual_limit?: number;
  /** Cents. Read from tio_limit.limits, NOT the legacy plan column. */
  lifetime_limit?: number;
}

/**
 * GET /plans/:id/summary — plan facts rendered for display, proxied to
 * tio-payment.
 *
 * The only route to a plan's real annual and lifetime limits: those come from
 * `tio_limit.limits`, while the `annualLimit`/`lifetimeLimit` attributes on the
 * plan resource are the superseded columns. tio-api gives the proxy a 10s
 * timeout and answers 503 when tio-payment cannot be reached, so call sites
 * need a degraded state rather than treating failure as "no limit".
 */
export function planSummaryRequest(planId: string): RequestInfo {
  return {
    url: plansUrl(`${planId}/summary`),
    method: 'GET',
    cacheOptions: { [SkipCache]: true },
  };
}
